import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Upload,
  Divider,
  List,
  Row,
  Col,
  DatePicker,
  Statistic,
  Descriptions,
  Empty,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PaperClipOutlined, FileOutlined, EyeOutlined, FileTextOutlined, SearchOutlined, FilePdfOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';

const Personal = () => {
  const { user } = useAuth();
  const [personal, setPersonal] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;
  const [modalVisible, setModalVisible] = useState(false);
  const [documentosModalVisible, setDocumentosModalVisible] = useState(false);
  const [editingTrabajador, setEditingTrabajador] = useState(null);
  const [selectedPersonal, setSelectedPersonal] = useState(null);
  const [documentosList, setDocumentosList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [resumenModalVisible, setResumenModalVisible] = useState(false);
  const [resumenData, setResumenData] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [selectedPersonalForResumen, setSelectedPersonalForResumen] = useState(null);
  const [resumenFecha, setResumenFecha] = useState(dayjs());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPersonal();
    if (isMainAdmin) {
      fetchEmpresas();
    }
  }, [isMainAdmin]);

  const fetchPersonal = async () => {
    setLoading(true);
    try {
      const response = await api.get('/personal');
      setPersonal(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error(error.message);
      setPersonal([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas');
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        fecha_ingreso: values.fecha_ingreso ? values.fecha_ingreso.format('YYYY-MM-DD') : null,
        fecha_cese: values.fecha_cese ? values.fecha_cese.format('YYYY-MM-DD') : null,
        periodo_vacacional_inicio: values.periodo_vacacional_inicio
          ? values.periodo_vacacional_inicio.format('YYYY-MM-DD')
          : null,
        periodo_vacacional_fin: values.periodo_vacacional_fin
          ? values.periodo_vacacional_fin.format('YYYY-MM-DD')
          : null,
      };
      if (editingTrabajador) {
        await api.put(`/personal/${editingTrabajador.id}`, payload);
        message.success('Trabajador actualizado exitosamente');
      } else {
        await api.post('/personal', payload);
        message.success('Trabajador creado exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingTrabajador(null);
      fetchPersonal();
    } catch (error) {
      const errorMessage = error.message;
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingTrabajador(record);
    form.setFieldsValue({
      ...record,
      empresa_id: record.empresa_id || undefined,
      fecha_ingreso: record.fecha_ingreso ? dayjs(record.fecha_ingreso) : null,
      fecha_cese: record.fecha_cese ? dayjs(record.fecha_cese) : null,
      periodo_vacacional_inicio: record.periodo_vacacional_inicio
        ? dayjs(record.periodo_vacacional_inicio)
        : null,
      periodo_vacacional_fin: record.periodo_vacacional_fin
        ? dayjs(record.periodo_vacacional_fin)
        : null,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/personal/${id}`);
      message.success('Trabajador eliminado exitosamente');
      fetchPersonal();
    } catch (error) {
      const errorMessage = error.message;
      message.error(errorMessage);
    }
  };

  const handleVerDocumentos = async (record) => {
    setSelectedPersonal(record);
    try {
      const response = await api.get(`/personal/${record.id}`);
      const documentos = response.data.documentos || [];
      setDocumentos(response.data);
      setModalDocumentosVisible(true);
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleUploadDocumentos = async () => {
    if (fileList.length === 0) {
      message.warning('Selecciona al menos un archivo para subir');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('imagenes[]', file.originFileObj);
        }
      });
      formData.append('tipo', 'personal');
      formData.append('field_name', 'imagenes');

      const uploadResponse = await api.post('/personal/upload-documentos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (uploadResponse.data.files && uploadResponse.data.files.length > 0) {
        // Agregar cada documento al personal
        for (const file of uploadResponse.data.files) {
          await api.post(`/personal/add-document/${selectedPersonal.id}`, {
            url: file.url,
            name: file.name,
            type: file.type,
            size: file.size,
          });
        }

        message.success('Documentos subidos exitosamente');
        setFileList([]);
        // Recargar documentos
        const response = await api.get(`/personal/${selectedPersonal.id}`);
        const documentos = response.data.documentos || [];
        setDocumentosList(documentos);
      } else {
        message.error('No se pudieron subir los archivos');
      }
    } catch (error) {
      console.error('Error al subir documentos:', error);
      message.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocumento = async (documentoUrl) => {
    try {
      await api.delete(`/personal/delete-document/${selectedPersonal.id}`, {
        data: { url: documentoUrl },
      });
      message.success('Documento eliminado exitosamente');
      // Recargar documentos
      const response = await api.get(`/personal/${selectedPersonal.id}`);
      const documentos = response.data.documentos || [];
      setDocumentosList(documentos);
    } catch (error) {
      message.error(error.message);
    }
  };

  const getFileIcon = (type) => {
    if (type && type.includes('image')) {
      return <FileOutlined style={{ color: '#1890ff' }} />;
    } else if (type && type.includes('pdf')) {
      return <FileOutlined style={{ color: '#ff4d4f' }} />;
    } else if (type && (type.includes('word') || type.includes('document'))) {
      return <FileOutlined style={{ color: '#1890ff' }} />;
    }
    return <FileOutlined />;
  };

  const handleVerResumenDiario = (record) => {
    setSelectedPersonalForResumen(record);
    setResumenFecha(dayjs());
    setResumenModalVisible(true);
    fetchResumenDiario(record.id, dayjs().format('YYYY-MM-DD'));
  };

  const fetchResumenDiario = async (personalId, fecha) => {
    setLoadingResumen(true);
    try {
      const response = await api.get(`/personal/resumen-diario/${personalId}?fecha=${fecha}`);
      setResumenData(response.data);
    } catch (error) {
      console.error('Error al cargar resumen:', error);
      message.error(error.message);
      setResumenData(null);
    } finally {
      setLoadingResumen(false);
    }
  };

  const handleFechaResumenChange = (fecha) => {
    if (!fecha) {
      return; // No hacer nada si la fecha es null
    }
    setResumenFecha(fecha);
    if (selectedPersonalForResumen) {
      fetchResumenDiario(selectedPersonalForResumen.id, fecha.format('YYYY-MM-DD'));
    }
  };

  const handleExportarPDF = async () => {
    if (!resumenData || !selectedPersonalForResumen) {
      message.warning('No hay datos para exportar');
      return;
    }

    try {
      message.loading({ content: 'Generando PDF...', key: 'pdf', duration: 0 });

      // Crear un elemento temporal para el PDF
      const printContent = document.createElement('div');
      printContent.style.padding = '20px';
      printContent.style.backgroundColor = 'white';
      printContent.style.width = '210mm'; // A4 width
      printContent.style.fontFamily = 'Arial, sans-serif';

      // Encabezado
      const header = document.createElement('div');
      header.style.marginBottom = '20px';
      header.style.borderBottom = '2px solid #1890ff';
      header.style.paddingBottom = '10px';
      header.innerHTML = `
        <h1 style="margin: 0; color: #1890ff; font-size: 24px;">Resumen Diario</h1>
        <p style="margin: 5px 0; color: #666; font-size: 14px;"><strong>Personal:</strong> ${selectedPersonalForResumen.nombre_completo}</p>
        <p style="margin: 5px 0; color: #666; font-size: 14px;"><strong>Fecha:</strong> ${resumenFecha.format('DD/MM/YYYY')}</p>
      `;
      printContent.appendChild(header);

      // Resumen de Caja Chica
      const cajaChicaSection = document.createElement('div');
      cajaChicaSection.style.marginBottom = '20px';
      cajaChicaSection.innerHTML = `
        <h2 style="color: #1890ff; border-bottom: 1px solid #ddd; padding-bottom: 5px; font-size: 18px; margin-bottom: 15px;">Resumen de Caja Chica</h2>
        <div style="display: flex; justify-content: space-around; margin-top: 15px;">
          <div style="text-align: center; border: 1px solid #ddd; padding: 15px; border-radius: 5px; flex: 1; margin: 0 5px;">
            <p style="margin: 0; color: #666; font-size: 12px;">Monto Asignado</p>
            <p style="margin: 5px 0; font-size: 18px; font-weight: bold; color: #1890ff;">S/ ${parseFloat(resumenData.caja_chica?.monto_asignado || 0).toFixed(2)}</p>
          </div>
          <div style="text-align: center; border: 1px solid #ddd; padding: 15px; border-radius: 5px; flex: 1; margin: 0 5px;">
            <p style="margin: 0; color: #666; font-size: 12px;">Total Gastos</p>
            <p style="margin: 5px 0; font-size: 18px; font-weight: bold; color: #ff4d4f;">S/ ${parseFloat(resumenData.caja_chica?.total_gastos || 0).toFixed(2)}</p>
          </div>
          <div style="text-align: center; border: 1px solid #ddd; padding: 15px; border-radius: 5px; flex: 1; margin: 0 5px;">
            <p style="margin: 0; color: #666; font-size: 12px;">Monto Disponible</p>
            <p style="margin: 5px 0; font-size: 18px; font-weight: bold; color: #3f8600;">S/ ${parseFloat(resumenData.caja_chica?.monto_disponible || 0).toFixed(2)}</p>
          </div>
        </div>
      `;
      printContent.appendChild(cajaChicaSection);

      // Servicios Realizados
      const serviciosSection = document.createElement('div');
      serviciosSection.style.marginBottom = '20px';
      serviciosSection.innerHTML = `
        <h2 style="color: #1890ff; border-bottom: 1px solid #ddd; padding-bottom: 5px; font-size: 18px; margin-bottom: 15px;">Servicios Realizados</h2>
        <div style="margin-top: 10px; margin-bottom: 15px;">
          <p style="margin: 5px 0; font-size: 14px;"><strong>Total de Servicios:</strong> ${resumenData.servicios?.length || 0}</p>
          <p style="margin: 5px 0; font-size: 14px;"><strong>Total Ingresos:</strong> S/ ${parseFloat(resumenData.total_ingresos || 0).toFixed(2)}</p>
        </div>
      `;

      // Tabla de servicios
      if (resumenData.servicios && resumenData.servicios.length > 0) {
        const table = document.createElement('table');
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
        table.style.marginTop = '15px';
        table.style.fontSize = '10px';

        // Encabezados
        const thead = document.createElement('thead');
        thead.style.backgroundColor = '#f0f0f0';
        thead.innerHTML = `
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">ID</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Cliente</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Tipo de Servicio</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Lugar Contacto</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Costo</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Estado</th>
          </tr>
        `;
        table.appendChild(thead);

        // Filas
        const tbody = document.createElement('tbody');
        resumenData.servicios.forEach((servicio, index) => {
          const tr = document.createElement('tr');
          tr.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9f9f9';
          tr.innerHTML = `
            <td style="border: 1px solid #ddd; padding: 6px;">${servicio.id}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${servicio.cliente_nombre || ''} ${servicio.cliente_apellido || ''}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${servicio.tipo_servicio_nombre || '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${servicio.lugar_contacto || '-'}</td>
            <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">S/ ${parseFloat(servicio.costo || 0).toFixed(2)}</td>
            <td style="border: 1px solid #ddd; padding: 6px;">${servicio.estado === 'cancelado' ? 'ANULADO' : (servicio.estado?.toUpperCase() || '-')}</td>
          `;
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);
        serviciosSection.appendChild(table);
      } else {
        serviciosSection.innerHTML += '<p style="color: #999; font-style: italic;">No hay servicios registrados para esta fecha</p>';
      }
      printContent.appendChild(serviciosSection);

      // Agregar al body temporalmente
      printContent.style.position = 'absolute';
      printContent.style.left = '-9999px';
      printContent.style.top = '0';
      document.body.appendChild(printContent);

      // Generar canvas
      const canvas = await html2canvas(printContent, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      // Limpiar
      document.body.removeChild(printContent);

      // Crear PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Descargar PDF
      const fileName = `Resumen_Diario_${selectedPersonalForResumen.nombre_completo.replace(/\s+/g, '_')}_${resumenFecha.format('YYYY-MM-DD')}.pdf`;
      pdf.save(fileName);

      message.success({ content: 'PDF generado exitosamente', key: 'pdf' });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      message.error({ content: 'Error al generar el PDF', key: 'pdf' });
    }
  };

  // Filtrar personal por nombre
  const filteredPersonal = personal.filter(p =>
    p.nombre_completo?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Nombre Completo',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
    },
    {
      title: 'Documento',
      dataIndex: 'documento',
      key: 'documento',
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
    },
    ...(isMainAdmin ? [{
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa',
      render: (empresa) => empresa || <Tag color="default">Sin empresa</Tag>,
    }] : []),
    {
      title: 'Tipo de Pago',
      dataIndex: 'tipo_pago',
      key: 'tipo_pago',
      render: (tipo) => {
        const tipos = {
          fijo: 'Fijo',
          por_servicio: 'Por Servicio',
          porcentaje: 'Porcentaje',
        };
        return tipos[tipo] || tipo;
      },
    },
    {
      title: 'Monto/Porcentaje',
      key: 'pago',
      render: (_, record) => {
        if (record.tipo_pago === 'fijo') {
          return `S/ ${record.monto_pago || 0}`;
        } else if (record.tipo_pago === 'por_servicio') {
          return `S/ ${record.monto_pago || 0}`;
        } else if (record.tipo_pago === 'porcentaje') {
          return `${record.porcentaje_pago || 0}%`;
        }
        return '-';
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          activo: 'green',
          inactivo: 'red',
        };
        return <Tag color={colors[estado]}>{estado === 'cancelado' ? 'ANULADO' : estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            <span className="hide-on-mobile">Editar</span>
          </Button>
          <Button
            type="default"
            icon={<PaperClipOutlined />}
            onClick={() => handleVerDocumentos(record)}
            size="small"
          >
            <span className="hide-on-mobile">Documentos</span>
          </Button>
          <Button
            type="default"
            icon={<FileTextOutlined />}
            onClick={() => handleVerResumenDiario(record)}
            size="small"
            style={{ color: '#1890ff' }}
          >
            <span className="hide-on-mobile">Resumen</span>
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este trabajador?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small">
              <span className="hide-on-mobile">Eliminar</span>
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Personal"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingTrabajador(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            <span className="hide-on-mobile">Nuevo Trabajador</span>
            <span className="show-on-mobile">Nuevo</span>
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Buscar por nombre..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ maxWidth: 300 }}
          />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={filteredPersonal}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Card>

      <Modal
        title={editingTrabajador ? 'Editar Trabajador' : 'Nuevo Trabajador'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingTrabajador(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="nombre_completo"
            label="Nombre Completo"
            rules={[{ required: true, message: 'Ingresa el nombre completo' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="documento"
            label="Documento"
            rules={[{ required: true, message: 'Ingresa el documento' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="codigo_trabajador" label="Código de Trabajador">
            <Input placeholder="Ej. AM009" />
          </Form.Item>
          <Form.Item
            name="cargo"
            label="Cargo"
            rules={[{ required: true, message: 'Ingresa el cargo' }]}
          >
            <Select placeholder="Selecciona el cargo">
              <Select.Option value="Gruero">Gruero</Select.Option>
              <Select.Option value="Mecánico">Mecánico</Select.Option>
              <Select.Option value="Chofer">Chofer</Select.Option>
              <Select.Option value="Operador">Operador</Select.Option>
              <Select.Option value="Otro">Otro</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="categoria" label="Categoría" initialValue="EMPLEADO">
            <Select>
              <Select.Option value="EMPLEADO">EMPLEADO</Select.Option>
              <Select.Option value="OBRERO">OBRERO</Select.Option>
              <Select.Option value="FUNCIONARIO">FUNCIONARIO</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="fecha_ingreso" label="Fecha de Ingreso">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="fecha_cese" label="Fecha de Cese">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="sistema_pensionario" label="Sistema Pensionario" initialValue="SNP">
            <Select>
              <Select.Option value="SNP">SNP (ONP)</Select.Option>
              <Select.Option value="SPP">SPP (AFP)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="cuspp" label="CUSPP">
            <Input placeholder="Solo si es SPP" />
          </Form.Item>
          <Form.Item name="periodicidad" label="Periodicidad" initialValue="MENSUAL">
            <Select>
              <Select.Option value="MENSUAL">MENSUAL</Select.Option>
              <Select.Option value="QUINCENAL">QUINCENAL</Select.Option>
              <Select.Option value="SEMANAL">SEMANAL</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="periodo_vacacional_inicio" label="Periodo Vacacional (Inicio)">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="periodo_vacacional_fin" label="Periodo Vacacional (Fin)">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="tipo_pago"
            label="Tipo de Pago"
            rules={[{ required: true, message: 'Selecciona el tipo de pago' }]}
          >
            <Select placeholder="Selecciona el tipo de pago">
              <Select.Option value="fijo">Fijo</Select.Option>
              <Select.Option value="por_servicio">Por Servicio</Select.Option>
              <Select.Option value="porcentaje">Porcentaje</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.tipo_pago !== currentValues.tipo_pago
            }
          >
            {({ getFieldValue }) => {
              const tipoPago = getFieldValue('tipo_pago');
              if (tipoPago === 'fijo' || tipoPago === 'por_servicio') {
                return (
                  <Form.Item
                    name="monto_pago"
                    label="Monto"
                    rules={[{ required: true, message: 'Ingresa el monto' }]}
                  >
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                );
              } else if (tipoPago === 'porcentaje') {
                return (
                  <Form.Item
                    name="porcentaje_pago"
                    label="Porcentaje"
                    rules={[{ required: true, message: 'Ingresa el porcentaje' }]}
                  >
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item name="telefono" label="Teléfono">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="direccion" label="Dirección">
            <Input.TextArea rows={2} />
          </Form.Item>
          {isMainAdmin && (
            <Form.Item name="empresa_id" label="Empresa">
              <Select placeholder="Selecciona una empresa" allowClear>
                {empresas.map((empresa) => (
                  <Select.Option key={empresa.id} value={empresa.id}>
                    {empresa.nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="estado" label="Estado" initialValue="activo">
            <Select>
              <Select.Option value="activo">Activo</Select.Option>
              <Select.Option value="inactivo">Inactivo</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                {editingTrabajador ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingTrabajador(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Documentos de ${selectedPersonal?.nombre_completo || ''}`}
        open={documentosModalVisible}
        onCancel={() => {
          setDocumentosModalVisible(false);
          setSelectedPersonal(null);
          setDocumentosList([]);
          setFileList([]);
        }}
        footer={null}
        width={700}
      >
        <Divider>Subir Nuevos Documentos</Divider>
        <Upload
          multiple
          fileList={fileList}
          onChange={({ fileList: newFileList }) => {
            setFileList(newFileList);
          }}
          beforeUpload={() => false}
          accept="image/*,.pdf,.doc,.docx"
        >
          <Button icon={<PlusOutlined />}>Seleccionar Archivos</Button>
        </Upload>
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={handleUploadDocumentos}
            loading={uploading}
            disabled={fileList.length === 0}
          >
            Subir Documentos
          </Button>
        </div>

        <Divider>Documentos Existentes</Divider>
        {documentosList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No hay documentos adjuntos
          </div>
        ) : (
          <List
            dataSource={documentosList}
            renderItem={(item, index) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => window.open(item.url, '_blank')}
                  >
                    Ver
                  </Button>,
                  <Popconfirm
                    title="¿Estás seguro de eliminar este documento?"
                    onConfirm={() => handleDeleteDocumento(item.url)}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button type="link" danger icon={<DeleteOutlined />}>
                      Eliminar
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={getFileIcon(item.type)}
                  title={item.name || `Documento ${index + 1}`}
                  description={
                    <div>
                      <div>Tipo: {item.type || 'Desconocido'}</div>
                      {item.size && <div>Tamaño: {(item.size / 1024).toFixed(2)} KB</div>}
                      {item.fecha && <div>Fecha: {new Date(item.fecha).toLocaleDateString()}</div>}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Modal de Resumen Diario */}
      <Modal
        title={`Resumen Diario - ${selectedPersonalForResumen?.nombre_completo || ''}`}
        open={resumenModalVisible}
        onCancel={() => {
          setResumenModalVisible(false);
          setSelectedPersonalForResumen(null);
          setResumenData(null);
        }}
        footer={[
          <Button key="exportar" type="primary" icon={<FilePdfOutlined />} onClick={handleExportarPDF} disabled={!resumenData || loadingResumen}>
            Exportar a PDF
          </Button>,
          <Button key="cerrar" onClick={() => {
            setResumenModalVisible(false);
            setSelectedPersonalForResumen(null);
            setResumenData(null);
          }}>
            Cerrar
          </Button>,
        ]}
        width="95%"
        style={{ maxWidth: 1000 }}
      >
        <div style={{ marginBottom: 16 }}>
          <DatePicker
            value={resumenFecha}
            onChange={handleFechaResumenChange}
            format="DD/MM/YYYY"
            style={{ width: '100%', maxWidth: 250 }}
            allowClear={false}
          />
        </div>

        {loadingResumen ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <p>Cargando resumen...</p>
          </div>
        ) : resumenData ? (
          <>
            <Divider orientation="left">Resumen de Caja Chica</Divider>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Monto Asignado"
                    value={resumenData.caja_chica?.monto_asignado || 0}
                    prefix="S/"
                    precision={2}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Total Gastos"
                    value={resumenData.caja_chica?.total_gastos || 0}
                    prefix="S/"
                    precision={2}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Monto Disponible"
                    value={resumenData.caja_chica?.monto_disponible || 0}
                    prefix="S/"
                    precision={2}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
            </Row>

            <Divider orientation="left">Servicios Realizados</Divider>
            {resumenData.servicios && resumenData.servicios.length > 0 ? (
              <>
                <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Total de Servicios">
                    {resumenData.servicios.length}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Ingresos">
                    S/ {resumenData.total_ingresos?.toFixed(2) || '0.00'}
                  </Descriptions.Item>
                </Descriptions>
                <div style={{ overflowX: 'auto' }}>
                  <Table
                    columns={[
                      {
                        title: 'ID',
                        dataIndex: 'id',
                        key: 'id',
                        width: 80,
                      },
                      {
                        title: 'Cliente',
                        key: 'cliente',
                        render: (_, record) => `${record.cliente_nombre || ''} ${record.cliente_apellido || ''}`,
                      },
                      {
                        title: 'Tipo de Servicio',
                        dataIndex: 'tipo_servicio_nombre',
                        key: 'tipo_servicio',
                      },
                      {
                        title: 'Lugar Contacto',
                        dataIndex: 'lugar_contacto',
                        key: 'lugar_contacto',
                      },
                      {
                        title: 'Costo',
                        dataIndex: 'costo',
                        key: 'costo',
                        render: (costo) => `S/ ${parseFloat(costo || 0).toFixed(2)}`,
                      },
                      {
                        title: 'Estado',
                        dataIndex: 'estado',
                        key: 'estado',
                        render: (estado) => {
                          const colors = {
                            pendiente: 'orange',
                            en_proceso: 'blue',
                            completado: 'green',
                            cancelado: 'red',
                          };
                          return <Tag color={colors[estado]}>{estado?.toUpperCase()}</Tag>;
                        },
                      },
                    ]}
                    dataSource={resumenData.servicios}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                  />
                </div>
              </>
            ) : (
              <Empty description="No hay servicios registrados para esta fecha" />
            )}
          </>
        ) : (
          <Empty description="No se pudo cargar el resumen" />
        )}
      </Modal>
    </div>
  );
};

export default Personal;

